import React, { useState } from 'react';
import { LockIcon, CalendarIcon, BuildingIcon, KeyIcon, FileTextIcon, PlusIcon, TrashIcon, CheckIcon, PaletteIcon, CreditCardIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { ResourcesPanel, UsageTypesPanel, ContractsPanel, PaymentsPanel } from './loisirs/ConfigPanels';
type Panel = 'ressources' | 'utilisation' | 'prealables' | 'sessions' | 'organismes' | 'contrats' | 'paiements' | 'acces' | 'documents';

/** Prerequisite kinds an activity can require, configured once and reused. */
const PREREQUISITE_KINDS = [{
  id: 'age',
  label: {
    fr: 'Tranche d’âge',
    en: 'Age range'
  },
  detail: {
    fr: 'Calculée au 31 décembre de l’année de la session.',
    en: 'Calculated as of December 31 of the session year.'
  },
  active: true
}, {
  id: 'level',
  label: {
    fr: 'Niveau précédent réussi',
    en: 'Previous level passed'
  },
  detail: {
    fr: 'Vérifie l’historique d’inscription du citoyen.',
    en: 'Checks the citizen’s registration history.'
  },
  active: true
}, {
  id: 'residency',
  label: {
    fr: 'Statut de résidence',
    en: 'Residency status'
  },
  detail: {
    fr: 'Priorité aux résidents pendant la période réservée.',
    en: 'Residents get priority during the reserved window.'
  },
  active: true
}, {
  id: 'document',
  label: {
    fr: 'Document obligatoire',
    en: 'Required document'
  },
  detail: {
    fr: 'Certificat médical, autorisation parentale, etc.',
    en: 'Medical certificate, parental authorization, etc.'
  },
  active: true
}, {
  id: 'membership',
  label: {
    fr: 'Abonnement valide',
    en: 'Valid membership'
  },
  detail: {
    fr: 'Exige un abonnement en règle au moment de l’inscription.',
    en: 'Requires an active membership at registration time.'
  },
  active: false
}];
const SESSIONS = [{
  id: 'ses-1',
  name: {
    fr: 'Hiver 2026',
    en: 'Winter 2026'
  },
  registration: {
    fr: '2 – 16 décembre 2025',
    en: 'December 2 – 16, 2025'
  },
  priority: {
    fr: '48 h réservées aux résidents',
    en: '48 h reserved for residents'
  },
  status: 'active'
}, {
  id: 'ses-2',
  name: {
    fr: 'Printemps 2026',
    en: 'Spring 2026'
  },
  registration: {
    fr: '10 – 24 mars 2026',
    en: 'March 10 – 24, 2026'
  },
  priority: {
    fr: '48 h réservées aux résidents',
    en: '48 h reserved for residents'
  },
  status: 'upcoming'
}, {
  id: 'ses-3',
  name: {
    fr: 'Camps été 2026',
    en: 'Summer camps 2026'
  },
  registration: {
    fr: '14 – 28 avril 2026',
    en: 'April 14 – 28, 2026'
  },
  priority: {
    fr: '72 h réservées aux résidents',
    en: '72 h reserved for residents'
  },
  status: 'upcoming'
}];
const ORGANIZATIONS = [{
  id: 'org-1',
  name: 'Association de hockey mineur de Brossard',
  category: {
    fr: 'Sport — jeunesse',
    en: 'Sports — youth'
  },
  recognized: true,
  freeFacilities: true,
  equipmentLoan: true
}, {
  id: 'org-2',
  name: 'FC Brossard',
  category: {
    fr: 'Sport — jeunesse',
    en: 'Sports — youth'
  },
  recognized: true,
  freeFacilities: false,
  equipmentLoan: true
}, {
  id: 'org-3',
  name: 'Club de l’âge d’or de Brossard',
  category: {
    fr: 'Aînés',
    en: 'Seniors'
  },
  recognized: true,
  freeFacilities: true,
  equipmentLoan: true
}, {
  id: 'org-4',
  name: 'Maison des jeunes Kekpart',
  category: {
    fr: 'Jeunesse',
    en: 'Youth'
  },
  recognized: true,
  freeFacilities: true,
  equipmentLoan: true
}, {
  id: 'org-5',
  name: 'Comité des fêtes de quartier',
  category: {
    fr: 'Communautaire',
    en: 'Community'
  },
  recognized: false,
  freeFacilities: false,
  equipmentLoan: true
}];
const ACCESS_RULES = [{
  id: 'rule-1',
  label: {
    fr: 'Abonnement annuel',
    en: 'Annual membership'
  },
  detail: {
    fr: 'Accès illimité au complexe aquatique et à la salle d’entraînement.',
    en: 'Unlimited access to the aquatic complex and fitness room.'
  },
  price: {
    fr: '240 $ / an — résident',
    en: '$240 / yr — resident'
  },
  active: true
}, {
  id: 'rule-2',
  label: {
    fr: 'Carte 10 entrées',
    en: '10-entry card'
  },
  detail: {
    fr: 'Décrémentée à chaque passage au tourniquet.',
    en: 'Decremented at each turnstile scan.'
  },
  price: {
    fr: '65 $ — résident',
    en: '$65 — resident'
  },
  active: true
}, {
  id: 'rule-3',
  label: {
    fr: 'Bain libre à l’unité',
    en: 'Single open swim'
  },
  detail: {
    fr: 'Validé contre la carte citoyenne, tarif résident appliqué.',
    en: 'Validated against the citizen card, resident rate applied.'
  },
  price: {
    fr: '8 $ — résident',
    en: '$8 — resident'
  },
  active: true
}, {
  id: 'rule-4',
  label: {
    fr: 'Accès accompagnateur',
    en: 'Companion access'
  },
  detail: {
    fr: 'Gratuit pour l’accompagnateur d’une personne à mobilité réduite.',
    en: 'Free for the companion of a person with reduced mobility.'
  },
  price: {
    fr: 'Gratuit',
    en: 'Free'
  },
  active: true
}, {
  id: 'rule-5',
  label: {
    fr: 'Blocage si solde impayé',
    en: 'Block on unpaid balance'
  },
  detail: {
    fr: 'Refuse l’accès lorsqu’une facture de loisirs reste impayée.',
    en: 'Denies access when a recreation invoice is unpaid.'
  },
  price: {
    fr: '—',
    en: '—'
  },
  active: false
}];
const DOCUMENTS = [{
  id: 'doc-1',
  label: {
    fr: 'Fiche santé — camp de jour',
    en: 'Health form — day camp'
  },
  detail: {
    fr: 'Allergies, médication, contacts d’urgence, autorisation de baignade.',
    en: 'Allergies, medication, emergency contacts, swimming authorization.'
  },
  mandatory: true
}, {
  id: 'doc-2',
  label: {
    fr: 'Relevé 24 (frais de garde)',
    en: 'RL-24 slip (childcare)'
  },
  detail: {
    fr: 'Généré automatiquement en février pour l’année civile précédente.',
    en: 'Generated automatically in February for the previous calendar year.'
  },
  mandatory: true
}, {
  id: 'doc-3',
  label: {
    fr: 'Certificat médical',
    en: 'Medical certificate'
  },
  detail: {
    fr: 'Requis pour les sports de contact, valide 12 mois.',
    en: 'Required for contact sports, valid for 12 months.'
  },
  mandatory: false
}, {
  id: 'doc-4',
  label: {
    fr: 'Preuve de résidence',
    en: 'Proof of residency'
  },
  detail: {
    fr: 'Automatique si le compte bciti+ est déjà validé.',
    en: 'Automatic when the bciti+ account is already verified.'
  },
  mandatory: false
}];

/** Reusable toggle row used across the configuration panels. */
function ToggleRow({
  title,
  detail,
  trailing,
  enabled,
  onToggle,
  ariaLabel
}: {
  title: string;
  detail: string;
  trailing?: string;
  enabled: boolean;
  onToggle: () => void;
  ariaLabel: string;
}) {
  return <li className="flex items-start gap-4 justify-between rounded-lg border border-slate-200 bg-white px-4 py-3.5" data-id="element-3376" data-mp="20lvovev0a8fp">
      <div className="min-w-0" data-id="element-3377" data-mp="3q4kggqm0lafy">
        <p className="text-sm font-medium text-slate-800" data-id="element-3378" data-mp="3q3sl9gwbxeq7">{title}</p>
        <p className="text-xs text-slate-500 mt-0.5" data-id="element-3379" data-mp="3l5zdijfdocnt">{detail}</p>
      </div>
      <div className="flex items-center gap-3 shrink-0" data-id="element-3380" data-mp="3l3wr09dfp1nu">
        {trailing && <span className="text-xs font-medium text-slate-500 hidden sm:inline" data-id="element-3381" data-mp="2qykfjm4zakp">
            {trailing}
          </span>}
        <button type="button" role="switch" aria-checked={enabled} aria-label={ariaLabel} onClick={onToggle} className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:ring-offset-1 ${enabled ? 'bg-[#4F46E5]' : 'bg-slate-200'}`} data-id="element-3382" data-mp="375ygxg65bre5">
          <span aria-hidden="true" className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ${enabled ? 'translate-x-4' : 'translate-x-0'}`} data-id="element-3383" data-mp="vekp6v73ol95" />
        </button>
      </div>
    </li>;
}
interface ConfigurerProps {
  /** Panel driven from the module shell (guided demo); falls back to local state. */
  panel?: string;
  onPanelChange?: (panel: string) => void;
}
export function LoisirsConfigurer({
  panel: panelProp,
  onPanelChange
}: ConfigurerProps = {}) {
  const {
    t,
    lang
  } = useLanguage();
  const [localPanel, setLocalPanel] = useState<Panel>('ressources');
  const panel = panelProp as Panel ?? localPanel;
  const setPanel = (next: Panel) => {
    setLocalPanel(next);
    onPanelChange?.(next);
  };
  const [prerequisites, setPrerequisites] = useState(PREREQUISITE_KINDS);
  const [accessRules, setAccessRules] = useState(ACCESS_RULES);
  const [documents, setDocuments] = useState(DOCUMENTS);
  const money = (value: number) => value.toLocaleString(lang === 'fr' ? 'fr-CA' : 'en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0
  });
  const panels: {
    id: Panel;
    label: string;
    icon: typeof LockIcon;
  }[] = [{
    id: 'ressources',
    label: t('Ressources', 'Resources'),
    icon: BuildingIcon
  }, {
    id: 'utilisation',
    label: t('Types d’utilisation', 'Usage types'),
    icon: PaletteIcon
  }, {
    id: 'contrats',
    label: t('Contrats', 'Contracts'),
    icon: FileTextIcon
  }, {
    id: 'paiements',
    label: t('Paiements', 'Payments'),
    icon: CreditCardIcon
  }, {
    id: 'prealables',
    label: t('Préalables', 'Prerequisites'),
    icon: LockIcon
  }, {
    id: 'sessions',
    label: t('Sessions', 'Sessions'),
    icon: CalendarIcon
  }, {
    id: 'organismes',
    label: t('Organismes', 'Organizations'),
    icon: BuildingIcon
  }, {
    id: 'acces',
    label: t('Droits d’accès', 'Access entitlements'),
    icon: KeyIcon
  }, {
    id: 'documents',
    label: t('Documents', 'Documents'),
    icon: FileTextIcon
  }];
  return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-3384" data-mp="2v06dn1yjsf7q">
      <div className="flex flex-wrap items-center gap-2" data-id="element-3385" data-mp="9hkesnmwftyl">
        {panels.map(p => {
        const Icon = p.icon;
        const active = panel === p.id;
        return <button key={p.id} onClick={() => setPanel(p.id)} className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium border transition-colors ${active ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`} data-id="element-3386" data-mp="iafbgsyawhcr">
              <Icon className={`w-4 h-4 ${active ? 'text-[#4F46E5]' : 'text-slate-400'}`} data-id="element-3387" data-mp="119t3h5cgzi0m" />
              {p.label}
            </button>;
      })}
      </div>

      {panel === 'ressources' && <ResourcesPanel data-id="element-3388" data-mp="331em1oidwz8x" />}
      {panel === 'utilisation' && <UsageTypesPanel data-id="element-3389" data-mp="2yzkse06lwvi9" />}
      {panel === 'contrats' && <ContractsPanel data-id="element-3390" data-mp="2z146bk4siugv" />}
      {panel === 'paiements' && <PaymentsPanel data-id="element-3391" data-mp="2ouujtbgp6t52" />}

      {/* Prerequisites */}
      {panel === 'prealables' && <section className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-3392" data-mp="2swpwfc42wsy5">
          <div className="flex items-start justify-between gap-4 mb-5 flex-wrap" data-id="element-3393" data-mp="2sysk3phre9zp">
            <div data-id="element-3394" data-mp="2f5b1c6wwd1aq">
              <h2 className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-3395" data-mp="2f3qe72cwwkmw">
                {t('Types de préalables', 'Prerequisite types')}
              </h2>
              <p className="text-sm text-slate-500 mt-1 max-w-2xl" data-id="element-3396" data-mp="256loisjuy71c">
                {t('Les préalables activés ici deviennent disponibles lors de la création d’une activité. Un préalable bloquant empêche l’inscription ; sinon, il affiche un simple avertissement.', 'Prerequisites enabled here become available when creating an activity. A blocking prerequisite prevents registration; otherwise it shows a warning only.')}
              </p>
            </div>
            <button className="inline-flex items-center gap-2 px-3.5 py-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-lg transition-colors shrink-0" data-id="element-3397" data-mp="3vj1gq9tgvbzo">
              <PlusIcon className="w-4 h-4" data-id="element-3398" data-mp="3p2rjxfsq29f2" />
              {t('Nouveau préalable', 'New prerequisite')}
            </button>
          </div>
          <ul className="space-y-2" data-id="element-3399" data-mp="3k6aumi9sdp0e">
            {prerequisites.map(pre => <ToggleRow key={pre.id} title={pre.label[lang]} detail={pre.detail[lang]} enabled={pre.active} ariaLabel={pre.label[lang]} onToggle={() => setPrerequisites(prev => prev.map(p => p.id === pre.id ? {
          ...p,
          active: !p.active
        } : p))} data-id="element-3400" data-mp="3f77oeui2lcqu" />)}
          </ul>
        </section>}

      {/* Sessions */}
      {panel === 'sessions' && <section className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-3401" data-mp="ulxojymp2upf">
          <div className="flex items-start justify-between gap-4 mb-5 flex-wrap" data-id="element-3402" data-mp="11zq744118z4f">
            <div data-id="element-3403" data-mp="16wyinlroe4wp">
              <h2 className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-3404" data-mp="16yjc18297hsz">
                {t('Sessions et périodes d’inscription', 'Sessions and registration windows')}
              </h2>
              <p className="text-sm text-slate-500 mt-1 max-w-2xl" data-id="element-3405" data-mp="1gwz2r9xdcp1y">
                {t('Chaque session ouvre les inscriptions à une date précise, avec une fenêtre de priorité pour les résidents.', 'Each session opens registration on a set date, with a priority window for residents.')}
              </p>
            </div>
            <button className="inline-flex items-center gap-2 px-3.5 py-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-lg transition-colors shrink-0" data-id="element-3406" data-mp="1zmyq9sdk9w39">
              <PlusIcon className="w-4 h-4" data-id="element-3407" data-mp="28n3ll45hd412" />
              {t('Nouvelle session', 'New session')}
            </button>
          </div>
          <ul className="space-y-2" data-id="element-3408" data-mp="36b6x9mik3f38">
            {SESSIONS.map(session => <li key={session.id} className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 px-4 py-3.5" data-id="element-3409" data-mp="31df3emycpiwe">
                <div className="min-w-0" data-id="element-3410" data-mp="3b2ov41pa8zjz">
                  <p className="text-sm font-semibold text-slate-800 flex items-center gap-2 flex-wrap" data-id="element-3411" data-mp="3b3rmeaqip40f">
                    {session.name[lang]}
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${session.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`} data-id="element-3412" data-mp="2neywow0hnse9">
                      {session.status === 'active' ? t('en cours', 'active') : t('à venir', 'upcoming')}
                    </span>
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5" data-id="element-3413" data-mp="f47na5l3r426">
                    {t('Inscriptions : ', 'Registration: ')}
                    {session.registration[lang]} · {session.priority[lang]}
                  </p>
                </div>
                <button aria-label={t('Retirer la session', 'Remove session')} className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors shrink-0" data-id="element-3414" data-mp="a5wc9rj2mnid">
                  <TrashIcon className="w-4 h-4" data-id="element-3415" data-mp="12uk1tzaurz1q" />
                </button>
              </li>)}
          </ul>
        </section>}

      {/* Organizations */}
      {panel === 'organismes' && <section className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-3416" data-mp="t3fot54qmo9j">
          <h2 className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-3417" data-mp="1gu2xvtqe3jxo">
            {t('Organismes reconnus', 'Recognized organizations')}
          </h2>
          <p className="text-sm text-slate-500 mt-1 mb-5 max-w-2xl" data-id="element-3418" data-mp="1vhbdr7un8dfz">
            {t('La reconnaissance municipale détermine la gratuité des plateaux et l’accès au prêt d’équipement.', 'Municipal recognition determines free facility use and access to equipment loans.')}
          </p>
          <div className="overflow-x-auto" data-id="element-3419" data-mp="1hik540yjvjz8">
            <table className="w-full text-sm text-left" data-id="element-3420" data-mp="1hjv4lokyq5kj">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50" data-id="element-3421" data-mp="1cjqy8rdrqr81">
                <tr data-id="element-3422" data-mp="17rh0ip6ewmv7">
                  <th className="px-4 py-3 rounded-l-lg" data-id="element-3423" data-mp="17qxrxz2njatu">
                    {t('Organisme', 'Organization')}
                  </th>
                  <th className="px-4 py-3" data-id="element-3424" data-mp="12qtd0dhe6o5q">{t('Catégorie', 'Category')}</th>
                  <th className="px-4 py-3" data-id="element-3425" data-mp="xve1hs6ux4ch">{t('Reconnu', 'Recognized')}</th>
                  <th className="px-4 py-3" data-id="element-3426" data-mp="tshgjutugw5d">
                    {t('Plateaux gratuits', 'Free facilities')}
                  </th>
                  <th className="px-4 py-3 rounded-r-lg" data-id="element-3427" data-mp="otwncfd0fcqf">
                    {t('Prêt d’équipement', 'Equipment loans')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-3428" data-mp="35e8hxa011fb1">
                {ORGANIZATIONS.map(org => <tr key={org.id} data-id="element-3429" data-mp="39hocm0sjd5l3">
                    <td className="px-4 py-3 font-medium text-slate-900" data-id="element-3430" data-mp="3eg97rlsxkz2o">
                      {org.name}
                    </td>
                    <td className="px-4 py-3 text-slate-600" data-id="element-3431" data-mp="2hr09ij7axq4s">
                      {org.category[lang]}
                    </td>
                    {[org.recognized, org.freeFacilities, org.equipmentLoan].map((value, i) => <td key={i} className="px-4 py-3" data-id="element-3432" data-mp="2qou1xj1y3zmo" data-mpi={i}>
                          {value ? <CheckIcon className="w-4 h-4 text-emerald-600" data-id="element-3433" data-mp="2vj85i8qrhd2f" /> : <span className="text-slate-300" data-id="element-3434" data-mp="2312rkkzytwo1">—</span>}
                        </td>)}
                  </tr>)}
              </tbody>
            </table>
          </div>
        </section>}



      {/* Access entitlements */}
      {panel === 'acces' && <section className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-3435" data-mp="15fzxb5f3oj9m">
          <h2 className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-3436" data-mp="11bs52y9fol6n">
            {t('Droits d’accès aux installations', 'Facility access entitlements')}
          </h2>
          <p className="text-sm text-slate-500 mt-1 mb-5 max-w-2xl" data-id="element-3437" data-mp="1adsz7m9der43">
            {t('Chaque droit est validé à la lecture de la carte citoyenne au tourniquet du complexe aquatique.', 'Each entitlement is validated when the citizen card is scanned at the aquatic complex turnstile.')}
          </p>
          <ul className="space-y-2" data-id="element-3438" data-mp="1y3vnrse2texy">
            {accessRules.map(rule => <ToggleRow key={rule.id} title={rule.label[lang]} detail={rule.detail[lang]} trailing={rule.price[lang]} enabled={rule.active} ariaLabel={rule.label[lang]} onToggle={() => setAccessRules(prev => prev.map(r => r.id === rule.id ? {
          ...r,
          active: !r.active
        } : r))} data-id="element-3439" data-mp="1t52bu4eftvzw" />)}
          </ul>
        </section>}

      {/* Documents */}
      {panel === 'documents' && <section className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-3440" data-mp="1ys4i47q469ob">
          <h2 className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-3441" data-mp="1k2q7kwasxm7c">
            {t('Documents et pièces justificatives', 'Documents and supporting files')}
          </h2>
          <p className="text-sm text-slate-500 mt-1 mb-5 max-w-2xl" data-id="element-3442" data-mp="1b079sqlvhzry">
            {t('Un document obligatoire bloque la confirmation de l’inscription tant qu’il n’est pas déposé.', 'A mandatory document blocks registration confirmation until it is submitted.')}
          </p>
          <ul className="space-y-2" data-id="element-3443" data-mp="1j6jfaddl8vb8">
            {documents.map(doc => <ToggleRow key={doc.id} title={doc.label[lang]} detail={doc.detail[lang]} trailing={doc.mandatory ? t('obligatoire', 'mandatory') : t('facultatif', 'optional')} enabled={doc.mandatory} ariaLabel={doc.label[lang]} onToggle={() => setDocuments(prev => prev.map(d => d.id === doc.id ? {
          ...d,
          mandatory: !d.mandatory
        } : d))} data-id="element-3444" data-mp="156qraptg4t6y" />)}
          </ul>
        </section>}
    </div>;
}